/**
 * Dashboard — Main authenticated landing page displaying user goals, progress, and quick actions.
 * @route /
 * @access Protected (requires authentication)
 *
 * Unified layout (mobile + desktop):
 *   1. GoalHeader (Welcome + Create + Generate Certificate)
 *   2. TodayHeroCard (smart auto-pick: overdue → today → next → empty)
 *   3. DailyHubPanel
 *   4. MainContent (goals, community, advertisers below goals)
 *
 * Mobile-only enhancements: animated mesh background, pull-to-refresh.
 */
import { useEffect, useState, useRef } from "react";
import { Sun, MapPin, Users, TrendingUp, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Section } from "@/components/common/Section";
import { useToast } from "@/hooks/use-toast";
import { GoalHeader } from "@/components/goals/GoalHeader";

import { AuthModalManager } from "@/components/auth/AuthModalManager";
import { checkDeadlines } from "@/utils/reminderUtils";
import { useCategories } from "@/hooks/useCategories";
import { useGoalState } from "@/hooks/goal-state";
import { useAuth } from "@/hooks/useAuth";
import { Goal } from "@/types/todo";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";
import { useNotificationState } from "@/hooks/useNotificationState";
import { PageLayout } from "@/components/common/PageLayout";
import { useIsMobile } from "@/hooks/use-mobile";
import { RoleSelectionBanner } from "@/components/auth/RoleSelectionBanner";
import { useRole } from "@/hooks/useRole";
import { OnboardingWizard } from "@/components/onboarding/OnboardingWizard";
import { LevelUpModal } from "@/components/gamification/LevelUpModal";
import { usePoints } from "@/hooks/usePoints";
import { NewGoalDialog } from "@/components/NewGoalDialog";
import { useQuickTasks } from "@/hooks/useQuickTasks";
import { DailyHubPanel } from "@/components/daily-hub/DailyHubPanel";

import { SectionErrorBoundary } from "@/components/SectionErrorBoundary";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useSEOMeta } from "@/hooks/useSEOMeta";
import { TodayHeroCard } from "@/components/dashboard/TodayHeroCard";
import { BucketPlanSpotlight } from "@/components/dashboard/BucketPlanSpotlight";
import { DiscoveryRadar } from "@/components/dashboard/DiscoveryRadar";

import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, ArrowDown } from "lucide-react";
import { getTimeSlot, toneClass } from "@/lib/timeOfDay";
import { LazyMount } from "@/components/common/LazyMount";
import { BackToTop } from "@/components/common/BackToTop";
import { CommunityCommitmentsWidget } from "@/components/dashboard/CommunityCommitmentsWidget";
import { ContinuePassportCard } from "@/components/dashboard/ContinuePassportCard";
import { DidYouKnowPopup } from "@/components/dashboard/DidYouKnowPopup";
import { DashboardSkeleton } from "@/components/dashboard/DashboardSkeleton";
import { AssignTile } from "@/components/assign/AssignTile";
import { JoinCommunityTile } from "@/components/dashboard/JoinCommunityTile";
import { ExploreMapHero } from "@/components/dashboard/HomeShortcutsRow";
import { AuthModal } from "@/components/auth/AuthModal";
import { CheckInChip } from "@/components/dashboard/CheckInChip";
import { WelcomeBonusPopup } from "@/components/dashboard/WelcomeBonusPopup";
import { AnnouncementBanner } from "@/components/dashboard/AnnouncementBanner";
import { HomeGoalsPreview } from "@/components/dashboard/HomeGoalsPreview";
import { YourProgressBlock } from "@/components/dashboard/YourProgressBlock";
import { RecommendedGoals } from "@/components/dashboard/RecommendedGoals";
import { AudienceEntryTiles } from "@/components/dashboard/AudienceEntryTiles";



const Index = () => {
  useDocumentTitle("My Goals");
  useSEOMeta({
    description: "Track personal goals, daily tasks, and progress. Your private GoalDigger dashboard for UAE families and students 13+.",
    canonicalPath: "/dashboard",
  });
  const { data: categoriesData, error: categoriesError } = useCategories();
  const { user, loading: authLoading } = useAuth();
  const { role, loading: roleLoading } = useRole();
  const { toast } = useToast();
  const { goals, addNewGoal, hasAcknowledged } = useGoalState();
  const { acknowledgeNotification } = useNotificationState();
  const isMobile = useIsMobile();
  const { showLevelUp, setShowLevelUp, newLevel, points: totalPoints } = usePoints();
  const { deleteTask } = useQuickTasks();
  const queryClient = useQueryClient();

  // Promoting quick task → goal
  const [promotingTask, setPromotingTask] = useState<{ text: string; id: string } | null>(null);


  // New-goal dialog (triggered from Today empty state)
  const [newGoalDialogOpen, setNewGoalDialogOpen] = useState(false);
  // Inline sign-in modal for guests
  const [authModalOpen, setAuthModalOpen] = useState(false);

  // Pull-to-refresh — mobile only, refetches goals + daily hub
  const containerRef = useRef<HTMLDivElement>(null);
  const { pull, progress, refreshing } = usePullToRefresh({
    enabled: isMobile,
    onRefresh: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['goals'] }),
        queryClient.invalidateQueries({ queryKey: ['cloudGoals'] }),
        queryClient.invalidateQueries({ queryKey: ['quickTasks'] }),
      ]);
      await new Promise((r) => setTimeout(r, 350));
    },
  });

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    const checkOnboardingStatus = async () => {
      // Single source of truth: the onboarding_completed flag.
      const hasCompletedOnboarding = localStorage.getItem("onboarding_completed");

      if (hasCompletedOnboarding) {
        setShowOnboarding(false);
        return;
      }

      // Guests (including anyone who just signed out) should never be
      // forced into the onboarding wizard — it saves to a signed-in profile.
      if (!user) {
        setShowOnboarding(false);
        return;
      }

      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('avatar, theme_color, onboarding_completed')
          .eq('id', user.id)
          .single();

        if (profile?.onboarding_completed || (profile?.avatar && profile?.theme_color)) {
          logger.debug("Onboarding: user has profile in DB, skipping wizard");
          localStorage.setItem("onboarding_completed", "true");
          if (profile.avatar) localStorage.setItem("user_avatar", profile.avatar);
          if (profile.theme_color) localStorage.setItem("user_color", profile.theme_color);
          setShowOnboarding(false);
          return;
        }

        const { count } = await supabase
          .from('goals')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', user.id);

        if (count && count > 0) {
          logger.debug("Onboarding: user has goals, skipping wizard");
          localStorage.setItem("onboarding_completed", "true");
          setShowOnboarding(false);
          return;
        }
      }

      logger.debug("Onboarding: showing wizard");
      setShowOnboarding(true);
    };

    checkOnboardingStatus();
  }, [authLoading, user]);

  const handleOnboardingComplete = async (firstGoal?: { title: string; category: string }) => {
    if (firstGoal && firstGoal.title !== "I'll Create My Own") {
      await addNewGoal({
        id: crypto.randomUUID(),
        title: firstGoal.title,
        category: firstGoal.category,
        progress: 0,
        dueDate: undefined,
        todos: [],
        isExample: false,
      });

      toast({
        title: "Goal Created! 🎯",
        description: `"${firstGoal.title}" has been added to your goals!`,
        duration: 4000,
      });

      await new Promise((resolve) => setTimeout(resolve, 500));
    }

    setShowOnboarding(false);
  };

  useEffect(() => {
    if (categoriesError) {
      toast({
        title: "Error loading categories",
        description: "There was an error loading the categories. Please try again later.",
        variant: "destructive",
      });
    }
  }, [categoriesError, toast]);

  useEffect(() => {
    const handleAcknowledgment = (event: CustomEvent<string>) => {
      acknowledgeNotification(event.detail);
    };

    document.addEventListener('acknowledgeNotification', handleAcknowledgment as EventListener);
    return () => {
      document.removeEventListener('acknowledgeNotification', handleAcknowledgment as EventListener);
    };
  }, [acknowledgeNotification]);

  useEffect(() => {
    if (!goals || goals.length === 0) return;

    const checkReminders = () => {
      const typedGoals: Goal[] = goals.map((goal) => ({
        id: goal.id || crypto.randomUUID(),
        title: goal.title,
        progress: goal.progress,
        dueDate: goal.dueDate,
        category: goal.category,
        todos: goal.todos.map((todo) => ({
          id: todo.id,
          text: todo.text,
          completed: todo.completed,
          dueDate: todo.dueDate,
          subtasks: todo.subtasks,
        })),
        isExample: goal.isExample || false,
      }));
      checkDeadlines(typedGoals, hasAcknowledged, acknowledgeNotification);
    };

    const initialTimeout = setTimeout(checkReminders, 1000);
    const interval = setInterval(checkReminders, 1000 * 60 * 60);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [goals, hasAcknowledged]);

  const handlePromoteToGoal = (taskText: string, taskId: string) => {
    setPromotingTask({ text: taskText, id: taskId });
  };

  const handleGoalCreatedFromTask = async (goal: Goal) => {
    await addNewGoal(goal);
    if (promotingTask) {
      deleteTask(promotingTask.id);
      toast({
        title: "Task promoted to goal! 🎯",
        description: `"${promotingTask.text}" is now a goal you can track.`,
      });
    }
    setPromotingTask(null);
  };

  const scrollToContent = () => {
    const target = document.getElementById('dashboard-content');
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <PageLayout
      bgClassName="bg-background page-polish gd-editorial"
      maxWidth="max-w-[1520px] w-full sm:px-6 lg:px-10"
      ariaLabel="Goal tracker main content"
    >
      {/* Pull-to-refresh indicator (mobile) */}
      {isMobile && (
        <div
          aria-hidden
          className="pointer-events-none fixed top-0 left-0 right-0 flex justify-center z-40"
          style={{
            height: `${pull}px`,
            opacity: pull > 0 || refreshing ? 1 : 0,
            transition: refreshing ? 'none' : 'opacity 0.2s, height 0.2s',
          }}
        >
          <div
            className="mt-2 h-9 w-9 rounded-full bg-white/95 shadow-md flex items-center justify-center"
            style={{ transform: `rotate(${progress * 360}deg)` }}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            ) : (
              <ArrowDown className="h-4 w-4 text-primary" />
            )}
          </div>
        </div>
      )}

      <div
        className={`${toneClass(getTimeSlot())} flex flex-col gap-8 sm:gap-10 lg:gap-14 pb-32 md:pb-16 w-full`}
        ref={containerRef}
      >
        {authLoading && <DashboardSkeleton />}
        {!authLoading && user && !roleLoading && !role && <RoleSelectionBanner />}

        {!authLoading && !user && (
          <div className="surface-raised overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <p className="t-title">You're exploring as a guest 👀</p>
                <p className="t-caption max-w-md">
                  Sign in to save goals, claim rewards, join community trails, and unlock the full map of offers.
                </p>
              </div>
              <Button
                variant="hero"
                size="pill"
                onClick={() => setAuthModalOpen(true)}
                className="shrink-0"
              >
                Sign in / Sign up
              </Button>
            </div>
          </div>
        )}

        <GoalHeader
          onGoalCreated={addNewGoal}
          categories={categoriesData?.map((cat) => cat.name) || []}
          user={user}
          showHeaderControls={false}
        />

        {/* Points HUD now lives inside the masthead (WelcomeBanner) */}

        {/* Admin broadcast banner — visible to guests + signed-in users */}
        <AnnouncementBanner />

        {user && <DidYouKnowPopup />}

        {/* Chapter 01 — Editorial spread: 8-col lead story + 4-col sticky lifestyle dossier */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 xl:gap-10 items-start">
          <div className="gd-deboxed lg:col-span-8 flex flex-col gap-8 lg:gap-10">
            <TodayHeroCard
              goals={goals}
              onAddGoal={() => setNewGoalDialogOpen(true)}
              onAddTodo={() => window.dispatchEvent(new CustomEvent('open-quick-tasks'))}
              onLogToday={scrollToContent}
            />
            <Section kicker="Chapter 01 · Today" title="Today's intentions" icon={<Sun />}>
              <SectionErrorBoundary section="Daily Hub">
                <DailyHubPanel onPromoteToGoal={handlePromoteToGoal} />
              </SectionErrorBoundary>
            </Section>
            <div id="dashboard-content" className="scroll-mt-24">
              <SectionErrorBoundary section="Your goals preview">
                <HomeGoalsPreview onCreateGoal={() => setNewGoalDialogOpen(true)} />
              </SectionErrorBoundary>
            </div>
          </div>
          <aside aria-label="Lifestyle dossier" className="lg:col-span-4 flex flex-col gap-6 lg:sticky lg:top-24">
            <p className="gd-kicker flex items-center gap-2">
              <span aria-hidden className="h-px w-6 bg-primary/50" />
              Lifestyle dossier
            </p>
            <SectionErrorBoundary section="Bucket plans">
              <BucketPlanSpotlight />
            </SectionErrorBoundary>
            <SectionErrorBoundary section="Discovery radar">
              <DiscoveryRadar />
            </SectionErrorBoundary>
          </aside>
        </div>

        {/* Chapter 02 — Full-bleed Explore spread */}
        <ExploreMapHero />

        {/* Chapter 03 — Journey (8) + Together (4) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 xl:gap-10 items-start">
          {user && (
            <div className="gd-deboxed lg:col-span-8">
              <Section kicker="Chapter 03 · Journey" title="Your progress" icon={<TrendingUp />}>
                <SectionErrorBoundary section="Your progress">
                  <YourProgressBlock />
                </SectionErrorBoundary>
                <LazyMount rootMargin="200px" placeholderMinHeight={60}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SectionErrorBoundary section="Continue passport">
                      <ContinuePassportCard />
                    </SectionErrorBoundary>
                    <SectionErrorBoundary section="Community commitments">
                      <CommunityCommitmentsWidget />
                    </SectionErrorBoundary>
                    <div className="md:col-span-2">
                      <SectionErrorBoundary section="Recommended goals">
                        <RecommendedGoals />
                      </SectionErrorBoundary>
                    </div>
                  </div>
                </LazyMount>
              </Section>
            </div>
          )}
          <div className={`gd-deboxed ${user ? "lg:col-span-4" : "lg:col-span-12"}`}>
            <SectionErrorBoundary section="Together section">
              <Section kicker={user ? "Together" : "Chapter 03 · Together"} title="Achieve together" icon={<Users />} gap="tight">
                <div className={user ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
                  <JoinCommunityTile />
                  <AssignTile />
                </div>
              </Section>
            </SectionErrorBoundary>
          </div>
        </div>

        {/* Chapter 04 — Partner with GoalDigger */}
        <SectionErrorBoundary section="Creator and business paths">
          <Section kicker="Chapter 04 · Partners" title="Partner with GoalDigger" icon={<Handshake />} gap="tight">
            <AudienceEntryTiles />
          </Section>
        </SectionErrorBoundary>



      </div>


      <BackToTop />

      <AuthModalManager user={user} authLoading={authLoading} />
      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />

      <OnboardingWizard
        open={showOnboarding}
        onOpenChange={setShowOnboarding}
        onComplete={handleOnboardingComplete}
      />

      <LevelUpModal
        open={showLevelUp}
        onOpenChange={setShowLevelUp}
        level={newLevel}
        totalPoints={totalPoints}
      />

      {user && <WelcomeBonusPopup />}




      <NewGoalDialog
        open={newGoalDialogOpen || !!promotingTask}
        onOpenChange={(open) => {
          if (!open) {
            setNewGoalDialogOpen(false);
            setPromotingTask(null);
          }
        }}
        onGoalCreated={promotingTask ? handleGoalCreatedFromTask : addNewGoal}
        categories={categoriesData?.map((cat) => cat.name) || []}
        initialTitle={promotingTask?.text}
      />
    </PageLayout>
  );
};

export default Index;
